import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTeam } from './TeamContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS, INSPECTION_ROUNDS } from '../data/nurseAppModels';

const EquipmentContext = createContext();

export const useEquipment = () => {
  const context = useContext(EquipmentContext);
  if (!context) {
    throw new Error('useEquipment must be used within EquipmentProvider');
  }
  return context;
};

export const EquipmentProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [equipment, setEquipment] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch equipment for current team
  useEffect(() => {
    if (!currentTeam) {
      setEquipment([]);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'equipment'),
        where('teamId', '==', currentTeam.id),
        orderBy('createdAt', 'desc')
      ),
      (snapshot) => {
        const equipmentData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setEquipment(equipmentData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching equipment:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentTeam]);

  // Fetch catalogs for current team
  useEffect(() => {
    if (!currentTeam) {
      setCatalogs([]);
      return;
    }

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'equipmentCatalogs'),
        where('teamId', '==', currentTeam.id),
        where('status', '==', 'ACTIVE'),
        orderBy('name', 'asc')
      ),
      (snapshot) => {
        const catalogsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCatalogs(catalogsData);
      },
      (err) => {
        console.error('Error fetching catalogs:', err);
      }
    );

    return () => unsubscribe();
  }, [currentTeam]);

  // Create Equipment from Catalog
  const createEquipmentFromCatalog = async (catalogId, additionalData = {}) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const catalogRef = doc(db, 'equipmentCatalogs', catalogId);
      const catalogSnap = await getDoc(catalogRef);

      if (!catalogSnap.exists()) {
        throw new Error('ไม่พบ Catalog');
      }

      const catalogData = catalogSnap.data();

      const newEquipment = {
        teamId: currentTeam.id,
        catalogId: catalogId,
        name: catalogData.name,
        code: catalogData.code,
        category: catalogData.category,
        subCategory: catalogData.subCategory,
        type: catalogData.type,
        size: additionalData.size || catalogData.size,
        unit: catalogData.unit,
        color: additionalData.color || catalogData.color,
        brand: catalogData.brand,
        model: catalogData.model,
        stockQuantity: additionalData.stockQuantity || 0,
        currentStock: additionalData.stockQuantity || 0,
        barcode: additionalData.barcode || null,
        serialNumber: additionalData.serialNumber || null,
        status: EQUIPMENT_STATUS.AVAILABLE,
        currentLocation: additionalData.currentLocation || catalogData.defaultLocation,
        department: additionalData.department || catalogData.defaultDepartment,
        ward: additionalData.ward || catalogData.defaultWard,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const equipmentRef = await addDoc(collection(db, 'equipment'), newEquipment);

      // Update catalog usage count
      await updateDoc(catalogRef, {
        usageCount: increment(1),
        lastUsedAt: serverTimestamp()
      });

      return { success: true, equipmentId: equipmentRef.id };
    } catch (err) {
      console.error('Error creating equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Create Equipment (without catalog)
  const createEquipment = async (equipmentData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const newEquipment = {
        teamId: currentTeam.id,
        name: equipmentData.name,
        code: equipmentData.code,
        category: equipmentData.category,
        subCategory: equipmentData.subCategory || null,
        type: equipmentData.type,
        size: equipmentData.size || null,
        unit: equipmentData.unit,
        color: equipmentData.color || null,
        brand: equipmentData.brand || null,
        model: equipmentData.model || null,
        stockQuantity: equipmentData.type === EQUIPMENT_TYPES.SMALL_ITEM ? equipmentData.stockQuantity || 0 : null,
        currentStock: equipmentData.type === EQUIPMENT_TYPES.SMALL_ITEM ? equipmentData.stockQuantity || 0 : null,
        barcode: equipmentData.type === EQUIPMENT_TYPES.LARGE_ITEM ? equipmentData.barcode || null : null,
        serialNumber: equipmentData.serialNumber || null,
        status: EQUIPMENT_STATUS.AVAILABLE,
        currentLocation: equipmentData.currentLocation || '',
        department: equipmentData.department || '',
        ward: equipmentData.ward || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const equipmentRef = await addDoc(collection(db, 'equipment'), newEquipment);

      return { success: true, equipmentId: equipmentRef.id };
    } catch (err) {
      console.error('Error creating equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Stock In Transaction
  const stockIn = async (equipmentId, transactionData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Create stock transaction
      const transaction = {
        teamId: currentTeam.id,
        equipmentId: equipmentId,
        type: 'STOCK_IN',
        quantity: transactionData.quantity,
        unit: equipmentData.unit,
        performedBy: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email,
        shiftRound: transactionData.shiftRound || null,
        date: transactionData.date || serverTimestamp(),
        source: transactionData.source || 'PURCHASE',
        sourceDetails: transactionData.sourceDetails || {},
        notes: transactionData.notes || '',
        status: 'PENDING',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const transactionRef = await addDoc(collection(db, 'equipmentStockTransactions'), transaction);

      return { success: true, transactionId: transactionRef.id };
    } catch (err) {
      console.error('Error creating stock in:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Stock In
  const approveStockIn = async (transactionId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const transactionRef = doc(db, 'equipmentStockTransactions', transactionId);
      const transactionSnap = await getDoc(transactionRef);

      if (!transactionSnap.exists()) {
        throw new Error('ไม่พบรายการ');
      }

      const transactionData = transactionSnap.data();
      const equipmentRef = doc(db, 'equipment', transactionData.equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Update equipment stock
      await updateDoc(equipmentRef, {
        currentStock: (equipmentData.currentStock || 0) + transactionData.quantity,
        updatedAt: serverTimestamp()
      });

      // Update transaction status
      await updateDoc(transactionRef, {
        status: 'APPROVED',
        approvedBy: currentUser.uid,
        approvedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error approving stock in:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Create Inspection
  const createInspection = async (inspectionData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const inspection = {
        teamId: currentTeam.id,
        date: inspectionData.date || serverTimestamp(),
        round: inspectionData.round,
        inspectedBy: currentUser.uid,
        inspectedByName: currentUser.displayName || currentUser.email,
        shiftType: inspectionData.shiftType,
        items: inspectionData.items || [],
        totalItems: inspectionData.items?.length || 0,
        totalAvailable: inspectionData.items?.reduce((sum, item) => sum + (item.availableQuantity || 0), 0) || 0,
        totalDamaged: inspectionData.items?.reduce((sum, item) => sum + (item.damagedQuantity || 0), 0) || 0,
        totalLost: inspectionData.items?.reduce((sum, item) => sum + (item.lostQuantity || 0), 0) || 0,
        notes: inspectionData.notes || '',
        status: 'COMPLETED',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const inspectionRef = await addDoc(collection(db, 'equipmentInspections'), inspection);

      return { success: true, inspectionId: inspectionRef.id };
    } catch (err) {
      console.error('Error creating inspection:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Borrow Equipment
  const borrowEquipment = async (equipmentId, borrowData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      if (equipmentData.type !== EQUIPMENT_TYPES.LARGE_ITEM) {
        throw new Error('สามารถยืมได้เฉพาะอุปกรณ์ชิ้นใหญ่');
      }

      // Create borrow request
      const borrowRequest = {
        teamId: currentTeam.id,
        equipmentId: equipmentId,
        type: 'BORROW',
        borrowedBy: currentUser.uid,
        borrowedByName: currentUser.displayName || currentUser.email,
        expectedReturnAt: borrowData.expectedReturnAt,
        notes: borrowData.notes || '',
        status: 'PENDING',
        createdAt: serverTimestamp()
      };

      const borrowRef = await addDoc(collection(db, 'equipmentBorrows'), borrowRequest);

      return { success: true, borrowId: borrowRef.id };
    } catch (err) {
      console.error('Error borrowing equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Approve Borrow
  const approveBorrow = async (borrowId) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์อนุมัติ');
      }

      const borrowRef = doc(db, 'equipmentBorrows', borrowId);
      const borrowSnap = await getDoc(borrowRef);

      if (!borrowSnap.exists()) {
        throw new Error('ไม่พบคำขอ');
      }

      const borrowData = borrowSnap.data();
      const equipmentRef = doc(db, 'equipment', borrowData.equipmentId);

      // Update equipment status
      await updateDoc(equipmentRef, {
        status: EQUIPMENT_STATUS.BORROWED,
        borrowedBy: borrowData.borrowedBy,
        borrowedByName: borrowData.borrowedByName,
        borrowedAt: serverTimestamp(),
        expectedReturnAt: borrowData.expectedReturnAt,
        updatedAt: serverTimestamp()
      });

      // Update borrow request
      await updateDoc(borrowRef, {
        status: 'APPROVED',
        approvedBy: currentUser.uid,
        approvedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error approving borrow:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Return Equipment
  const returnEquipment = async (equipmentId, returnData) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      const equipmentRef = doc(db, 'equipment', equipmentId);
      const equipmentSnap = await getDoc(equipmentRef);

      if (!equipmentSnap.exists()) {
        throw new Error('ไม่พบอุปกรณ์');
      }

      const equipmentData = equipmentSnap.data();

      // Create return record
      const returnRecord = {
        teamId: currentTeam.id,
        equipmentId: equipmentId,
        type: 'RETURN',
        returnedBy: currentUser.uid,
        returnedByName: currentUser.displayName || currentUser.email,
        condition: returnData.condition || 'GOOD',
        notes: returnData.notes || '',
        status: 'COMPLETED',
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'equipmentBorrows'), returnRecord);

      // Update equipment status
      const updateData = {
        status: returnData.condition === 'DAMAGED' ? EQUIPMENT_STATUS.DAMAGED : EQUIPMENT_STATUS.AVAILABLE,
        borrowedBy: null,
        borrowedByName: null,
        borrowedAt: null,
        expectedReturnAt: null,
        updatedAt: serverTimestamp()
      };

      await updateDoc(equipmentRef, updateData);

      return { success: true };
    } catch (err) {
      console.error('Error returning equipment:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const value = {
    equipment,
    catalogs,
    loading,
    error,
    createEquipment,
    createEquipmentFromCatalog,
    stockIn,
    approveStockIn,
    createInspection,
    borrowEquipment,
    approveBorrow,
    returnEquipment
  };

  return (
    <EquipmentContext.Provider value={value}>
      {children}
    </EquipmentContext.Provider>
  );
};


